<script lang="ts">
  /**
   * The superadmin accounts console — every person with a Sideguide login, on one
   * page, with what they pay us, what they bring us and whether they are still here.
   *
   * WHY IT IS ONE TABLE AND NOT A DASHBOARD. Staff open this to answer three
   * questions: who is paying, who is about to lapse, and who has gone quiet. All
   * three are per-row facts, so they belong in the row — a stat strip above the table
   * would only restate what the rows already say. The filter chips carry the counts
   * instead, which makes them the summary AND the way to act on it.
   *
   * THE PLAN COLUMN IS PER COMMUNITY, deliberately. Billing is per community
   * (`domain.billing.skool_plan_status`), so "what does this account pay" is really
   * "what do their communities pay", and an account-level roll-up would be a fourth
   * derivation of a state the server already computes. `plan_status` at the ACCOUNT
   * level appears once, as the "free for life" mark: these days it only reports the
   * manual `paid_at` lever, which is the thing a superadmin sets from here.
   *
   * ONE REQUEST. `?role=all&fields=*` in one walk, then search, filter and sort all
   * run in the browser over the loaded rows. That is the right trade at this size (a
   * few hundred accounts at most, all of them staff-visible): the alternative is a
   * round trip per keystroke for a table that fits in memory. The server-side `?q=`
   * exists for the pickers that must not download everything — this page is the one
   * that legitimately wants the whole set.
   */
  import { navigate } from '../lib/router.svelte';
  import AdminShell from './AdminShell.svelte';
  import PageContainer from '../lib/ui/PageContainer.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import PageHeader from '../lib/ui/PageHeader.svelte';
  import Drawer from '../lib/ui/Drawer.svelte';
  import PlanBadge from '../lib/ui/PlanBadge.svelte';
  import {
    redirectIfAwaitingOnboarding,
    getToken,
    handle401,
    parseTs,
    timeAgo,
    type AdminUser,
  } from '../lib/admin';
  import { fmtRate, fmtUsd } from '../lib/affiliates';
  import { type MeCommunity } from '@api';
import { asAdminUser, ensureBootstrap } from '../lib/session';
  import { createAccountInvite, fetchAllAccountInvites, fetchAllAccounts, fetchCommunities, patchAccount, revokeAccountInvite, type AccountInvite, type Account } from '@api';

  let me = $state<AdminUser | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let accounts = $state<Account[]>([]);
  // Only the links nobody has clicked yet. A used invite is history — the account it
  // created is in the table below, which is a better record of it than a spent token.
  let invites = $state<AccountInvite[]>([]);

  // One clock for every relative date on the page, read at load. Rows say "3 days
  // ago", not "3 days and 4 seconds ago", so a ticking timer would buy nothing and
  // re-render the whole table for it.
  const now = Date.now();
  const DORMANT_DAYS = 30;

  async function init(): Promise<void> {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    loading = true;
    error = null;
    let pageErr: unknown = null;
    // Fired WITH the bootstrap, not behind it: a creator who lands here spends one
    // swallowed 403 on the way to the redirect, which is cheaper than making every
    // superadmin wait a round trip to find out who is asking.
    const pending = Promise.all([
      fetchAllAccounts({ role: 'all', fields: ['*'] }),
      fetchAllAccountInvites({ status: 'pending' }),
    ]).catch((err: unknown) => {
      pageErr = err;
      return null;
    });
    try {
      me = asAdminUser(await ensureBootstrap());
      if (redirectIfAwaitingOnboarding(me, navigate)) return;
      if (me.role !== 'superadmin') {
        navigate('/admin');
        return;
      }
      const loaded = await pending;
      if (loaded) {
        accounts = loaded[0].items;
        invites = loaded[1].items;
      } else if (pageErr) throw pageErr;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load.';
    } finally {
      loading = false;
    }
  }
  void init();

  // --- row predicates ---------------------------------------------------------
  //
  // Each reads the COMMUNITY plan states, never the account-level one, for the
  // reason in the module comment. A superadmin holds no communities and so counts as
  // none of paying/trial/lapsed — which is true: staff are not billed.

  const has = (a: Account, ...states: string[]) =>
    (a.communities ?? []).some((c) => states.includes(c.plan_status));

  // `isPaying` is 'paid' ALONE, and that is the whole point: before #156 a comp also
  // derived 'paid', so this filter reported 7 paying accounts on prod when exactly one
  // was paying. Splitting the token fixed this line without editing it.
  const isPaying = (a: Account) => has(a, 'paid');
  const isFreeForLife = (a: Account) => has(a, 'free_for_life');
  const isTrial = (a: Account) => has(a, 'trial');
  const isLapsed = (a: Account) => has(a, 'expired', 'needs_payment');
  const isAffiliate = (a: Account) => !!a.affiliate;

  /** Quiet for a month, or never signed in at all. The one health signal on the
   *  page that is about the PERSON rather than their bill. */
  function isDormant(a: Account): boolean {
    if (!a.last_login_at) return true;
    return now - parseTs(a.last_login_at).getTime() > DORMANT_DAYS * 86400000;
  }

  // --- filter + search + sort -------------------------------------------------

  type Filter =
    | 'all' | 'paying' | 'free_for_life' | 'trial' | 'lapsed' | 'affiliates' | 'dormant';
  const FILTERS: { id: Filter; label: string; match: (a: Account) => boolean }[] = [
    { id: 'all', label: 'All', match: () => true },
    { id: 'paying', label: 'Paying', match: isPaying },
    { id: 'free_for_life', label: 'Free for life', match: isFreeForLife },
    { id: 'trial', label: 'On trial', match: isTrial },
    { id: 'lapsed', label: 'Lapsed', match: isLapsed },
    { id: 'affiliates', label: 'Affiliates', match: isAffiliate },
    { id: 'dormant', label: 'Dormant', match: isDormant },
  ];

  let filter = $state<Filter>('all');
  let query = $state('');

  type SortKey = 'name' | 'seen' | 'joined';
  let sortKey = $state<SortKey>('joined');
  let sortAsc = $state(false);

  function sortBy(key: SortKey): void {
    // Re-clicking the active column flips it; a new column starts descending, which
    // for all three of these means "most recent / last in the alphabet first".
    if (sortKey === key) sortAsc = !sortAsc;
    else {
      sortKey = key;
      sortAsc = false;
    }
  }

  /** Milliseconds, with "never" sorting BELOW every real timestamp in both
   *  directions — an account with no sign-in is not "oldest", it is unknown, and
   *  burying it keeps the top of the table meaningful. */
  const stamp = (iso: string | null | undefined) =>
    iso ? parseTs(iso).getTime() : Number.NEGATIVE_INFINITY;

  const counts = $derived.by(() => {
    const out: Record<string, number> = {};
    for (const f of FILTERS) out[f.id] = accounts.filter(f.match).length;
    return out;
  });

  const visible = $derived.by(() => {
    const term = query.trim().toLowerCase();
    const active = FILTERS.find((f) => f.id === filter) ?? FILTERS[0]!;
    const rows = accounts.filter((a) => {
      if (!active.match(a)) return false;
      if (!term) return true;
      // Search reaches the things a superadmin actually remembers an account by: the
      // person, their community, and their referral code.
      const hay = [
        a.name,
        a.email,
        a.affiliate?.code,
        a.referred_by?.code,
        ...(a.communities ?? []).map((c) => c.name),
      ];
      return hay.some((v) => v?.toLowerCase().includes(term));
    });
    const dir = sortAsc ? 1 : -1;
    return rows.sort((x, y) => {
      if (sortKey === 'name') return dir * (x.name ?? '').localeCompare(y.name ?? '');
      if (sortKey === 'seen') return dir * (stamp(x.last_login_at) - stamp(y.last_login_at));
      return dir * (stamp(x.created_at) - stamp(y.created_at));
    });
  });

  // --- presence ---------------------------------------------------------------
  //
  // The one signal in the row that is not a word. Four states, read left-to-right
  // before anything else on the row: filled = here this week, half = here this
  // month, faint = gone quiet, hollow ring = never signed in. The relative date
  // beside it says the same thing precisely; the dot is what makes fifty rows
  // scannable at once.
  function presence(a: Account): { cls: string; title: string } {
    if (!a.last_login_at) {
      return { cls: 'border border-base-content/30 bg-transparent', title: 'Never signed in' };
    }
    const days = (now - parseTs(a.last_login_at).getTime()) / 86400000;
    const title = `Last signed in ${timeAgo(a.last_login_at, now)}`;
    if (days <= 7) return { cls: 'bg-primary', title };
    if (days <= DORMANT_DAYS) return { cls: 'bg-primary/45', title };
    return { cls: 'bg-base-content/22', title };
  }

  const fmtDate = (iso: string | null | undefined) =>
    iso
      ? parseTs(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
      : '—';

  const lastSeen = (a: Account) => (a.last_login_at ? timeAgo(a.last_login_at, now) : 'never');

  // --- detail drawer ----------------------------------------------------------

  let openId = $state<number | null>(null);
  const selected = $derived(accounts.find((a) => a.user_id === openId) ?? null);
  let busy = $state(false);

  /** The PATCH returns the account in the same representation the list uses, so the
   *  row is patched in place and the table is never re-read (doc 02 W9). */
  function apply(updated: Account): void {
    accounts = accounts.map((a) => (a.user_id === updated.user_id ? updated : a));
  }

  async function toggleComp(a: Account): Promise<void> {
    // `plan: null` CLEARS the manual stamp — the toggle is its own undo, where an
    // omitted field would mean "leave it alone" (`AccountPatch`).
    const plan = a.plan_status === 'free_for_life' ? null : 'free_for_life';
    busy = true;
    try {
      apply(await patchAccount(a.user_id, { plan }));
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
    } finally {
      busy = false;
    }
  }

  // --- invite someone ---------------------------------------------------------
  //
  // A magic link for one email and one or more communities. The email need not be an
  // account yet — an unknown address gets one when the link is clicked, which is the
  // whole reason this lives here rather than behind the (broken) signup flow.

  let inviting = $state(false);          // dialog open
  let inviteEmail = $state('');
  let invitePicked = $state<Set<number>>(new Set());
  let inviteBusy = $state(false);
  let inviteError = $state<string | null>(null);
  let lastInvite = $state<AccountInvite | null>(null);
  // Set when the dialog was opened FROM a row. Two things change: the address is
  // fixed (a link minted from somebody's row must be for them, not for whoever the
  // field was last edited to say), and no community is required — an existing account
  // can be sent a plain sign-in link, which is the only way back in for somebody who
  // claimed an invite and therefore has no password.
  let inviteFor = $state<Account | null>(null);
  const inviteHolds = $derived(new Set((inviteFor?.communities ?? []).map((c) => c.id)));
  let copied = $state<string | null>(null);
  let copyTimer: ReturnType<typeof setTimeout> | undefined;

  /** The claim URL. Built here, not server-side: this API has no stored frontend
   *  origin, and the superadmin is looking at the same host the recipient will use —
   *  the same rule the registration links on /admin/invites follow. */
  const linkFor = (token: string) => `${location.origin}/invite?token=${token}`;

  async function copyLink(token: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(linkFor(token));
    } catch {
      return;
    }
    copied = token;
    clearTimeout(copyTimer);
    copyTimer = setTimeout(() => (copied = null), 1600);
  }

  /** Open the invite dialog. Pass an account to mint a link FOR that person — their
   *  address is filled in and locked, and the communities they already hold are shown
   *  as such so you only tick what you're adding. */
  function openInvite(account: Account | null): void {
    inviting = true;
    inviteFor = account;
    inviteEmail = account?.email ?? '';
    invitePicked = new Set();
    inviteError = null;
    lastInvite = null;
    commSearch = '';
    commResults = [];
    void searchCommunities('');
  }

  function toggleInvitePick(id: number): void {
    const n = new Set(invitePicked);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    invitePicked = n;
  }

  // A brand-new address must come with at least one community — a link that creates
  // an account and drops it into an empty panel helps nobody. An existing account may
  // take zero: that is the sign-in link. The server enforces the same rule; this just
  // stops the button being clickable when it would 422.
  const canSubmitInvite = $derived(
    !!inviteEmail.trim() && (invitePicked.size > 0 || !!inviteFor),
  );

  async function submitInvite(): Promise<void> {
    const email = inviteEmail.trim();
    if (inviteBusy || !canSubmitInvite) return;
    inviteBusy = true;
    inviteError = null;
    try {
      const row = await createAccountInvite({ email, skoolIds: [...invitePicked] });
      // The POST returns the invite, so the pending list is patched locally rather
      // than re-read (W9). Newest first is the list's own order.
      invites = [row, ...invites];
      lastInvite = row;
      await copyLink(row.token); // the link is the deliverable — copy it immediately
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      inviteError = err instanceof Error ? err.message : 'Could not create the invite.';
    } finally {
      inviteBusy = false;
    }
  }

  async function revokeInvite(inv: AccountInvite): Promise<void> {
    if (!confirm(`Revoke the invite for ${inv.email}? The link stops working immediately.`)) {
      return;
    }
    try {
      await revokeAccountInvite(inv.id);
      invites = invites.filter((i) => i.id !== inv.id);
      if (lastInvite?.id === inv.id) lastInvite = null;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Could not revoke that invite.';
    }
  }

  /** "expires in 6 days" / "tomorrow" / "today" — how long a pending link has left.
   *
   *  ROUND, not ceil. `now` is read once at page load, so a link minted a minute later
   *  is 7 days AND a minute out — and ceil turns that into "expires in 8 days" for a
   *  7-day invite, which contradicts the sentence right next to it. Rounding reads the
   *  duration as a person would; `Date.now()` rather than `now` keeps a link created
   *  during this page's life honest without re-rendering the table. */
  function expiresIn(iso: string | null): string {
    if (!iso) return '';
    const days = Math.round((parseTs(iso).getTime() - Date.now()) / 86400000);
    if (days <= 0) return 'expires today';
    if (days === 1) return 'expires tomorrow';
    return `expires in ${days} days`;
  }

  // --- manage communities -----------------------------------------------------
  //
  // Which communities a creator may administer. The picker searches the fleet
  // SERVER-side, one page at a time (`GET /v1/communities?q=`), because `/v1/me`
  // deliberately never returns the fleet — not even to a superadmin.
  let editing = $state<Account | null>(null);
  let picked = $state<Set<number>>(new Set());
  let commSearch = $state('');
  let commResults = $state<MeCommunity[]>([]);
  let commLoading = $state(false);
  let saving = $state(false);
  let saveError = $state<string | null>(null);
  let commTimer: ReturnType<typeof setTimeout> | undefined;

  // The account's CURRENT communities are always offered, whatever the search says —
  // otherwise a ticked checkbox could scroll out of existence and saving would
  // silently revoke it.
  const commMatches = $derived.by((): { id: number; name: string; slug: string }[] => {
    const seen = new Set<number>();
    const out: { id: number; name: string; slug: string }[] = [];
    for (const c of [...(editing?.communities ?? []), ...commResults]) {
      if (seen.has(c.id)) continue;
      seen.add(c.id);
      out.push({ id: c.id, name: c.name, slug: c.slug });
    }
    return out;
  });

  function openCommunities(a: Account): void {
    editing = a;
    picked = new Set((a.communities ?? []).map((c) => c.id));
    commSearch = '';
    commResults = [];
    saveError = null;
    void searchCommunities('');
  }

  function onCommSearch(e: Event): void {
    commSearch = (e.target as HTMLInputElement).value;
    clearTimeout(commTimer);
    const term = commSearch;
    commLoading = true;
    commTimer = setTimeout(() => void searchCommunities(term), 250);
  }

  async function searchCommunities(term: string): Promise<void> {
    commLoading = true;
    try {
      commResults = (await fetchCommunities({ q: term, limit: 50 })).items;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      commResults = [];
    } finally {
      commLoading = false;
    }
  }

  function togglePick(id: number): void {
    const n = new Set(picked);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    picked = n;
  }

  async function saveCommunities(): Promise<void> {
    if (!editing) return;
    const ids = [...picked];
    // Revoking everything locks the account out of the admin panel entirely — worth
    // a confirm rather than a silent success.
    if (
      ids.length === 0 &&
      !confirm(`Remove every community from ${editing.email}? They'll lose access to the admin panel.`)
    ) {
      return;
    }
    saving = true;
    saveError = null;
    try {
      // The full desired set, not a delta — which is what a checkbox list produces
      // and what the PATCH takes. The response is the updated account (W9).
      apply(await patchAccount(editing.user_id, { skool_ids: ids }));
      editing = null;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      saveError = err instanceof Error ? err.message : 'Could not save communities.';
    } finally {
      saving = false;
    }
  }
</script>

{#snippet sortHead(key: SortKey, label: string, extra: string)}
  <!-- `extra` carries the responsive class for this column. Narrow screens drop the
       columns the drawer repeats (affiliate, joined, last seen) rather than pushing
       them behind a horizontal scroll nobody discovers; the presence dot keeps the
       one signal that can't be spelled out in the space left. -->
  <th class="font-normal {extra}">
    <button
      class="inline-flex items-center gap-1 transition-colors hover:text-base-content {sortKey === key
        ? 'text-base-content'
        : ''}"
      onclick={() => sortBy(key)}
      aria-label="Sort by {label}"
    >
      {label}
      <span class="text-[0.6rem] {sortKey === key ? 'opacity-70' : 'opacity-0'}">
        {sortAsc ? '▲' : '▼'}
      </span>
    </button>
  </th>
{/snippet}

{#snippet affiliateCell(a: Account)}
  {#if a.affiliate}
    <div class="flex items-center gap-1.5">
      <span class="redact rounded bg-base-200/70 px-1.5 py-px font-mono text-[0.65rem]">{a.affiliate.code}</span>
      {#if a.affiliate.disabled}
        <span class="eyebrow text-[0.55rem] text-base-content/55">off</span>
      {/if}
    </div>
    <div class="mt-0.5 text-[0.7rem] text-base-content/62">
      {a.affiliate.referred_count}
      {a.affiliate.referred_count === 1 ? 'referral' : 'referrals'} ·
      <span class="redact">{fmtUsd(a.affiliate.totals.lifetime_cents)}</span>
    </div>
  {:else if a.referred_by}
    <span class="redact text-[0.75rem] text-base-content/62">via {a.referred_by.name}</span>
  {:else}
    <span class="text-base-content/38">—</span>
  {/if}
{/snippet}

<AdminShell {me}>
  {#if loading}
    <StateBlock state="loading" />
  {:else if error}
    <StateBlock state="error" message={error} onretry={init} />
  {:else}
    <PageContainer>
      <PageHeader section="Super admin" title="Accounts">
        {#snippet actions()}
          <button class="btn btn-primary btn-sm" onclick={() => openInvite(null)}>Invite someone</button>
        {/snippet}
      </PageHeader>

      <!-- Links nobody has clicked yet. Shown only when there are any: an empty box
           saying "no pending invites" would be a permanent reminder of nothing. -->
      {#if invites.length > 0}
        <section class="rounded-box border hair bg-base-100">
          <div class="eyebrow border-b hair px-4 py-2.5">Waiting to be accepted</div>
          <ul>
            {#each invites as inv (inv.id)}
              <li class="flex flex-wrap items-center gap-x-3 gap-y-1 border-b hair px-4 py-2.5 last:border-b-0">
                <span class="redact font-mono text-xs">{inv.email}</span>
                <span class="flex flex-wrap items-center gap-1">
                  {#each inv.communities as c (c.id)}
                    <span class="redact rounded bg-base-200/70 px-1.5 py-px text-xs">{c.name}</span>
                  {/each}
                </span>
                <span class="text-[0.7rem] text-base-content/55">{expiresIn(inv.expires_at)}</span>
                <span class="ml-auto flex items-center gap-1">
                  <button class="btn btn-ghost btn-xs" onclick={() => copyLink(inv.token)}>
                    {copied === inv.token ? 'Copied!' : 'Copy link'}
                  </button>
                  <button class="btn btn-ghost btn-xs text-error/80" onclick={() => revokeInvite(inv)}>
                    Revoke
                  </button>
                </span>
              </li>
            {/each}
          </ul>
        </section>
      {/if}

      <!-- Filters double as the summary: the counts live on the chips, so the page
           doesn't need a stat strip restating what the rows already say.
           The top margin is conditional because the header owns the gap below itself:
           this row is the first block when there are no pending invites. -->
      <div
        class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
        class:mt-6={invites.length > 0}
      >
        <div class="flex flex-wrap items-center gap-1.5">
          {#each FILTERS as f (f.id)}
            <button
              class="rounded-field border px-2.5 py-1 text-xs transition-colors
                {filter === f.id
                ? 'border-primary/30 bg-primary/10 font-medium text-primary'
                : 'hair text-base-content/70 hover:bg-base-200/60'}"
              aria-pressed={filter === f.id}
              onclick={() => (filter = f.id)}
            >
              {f.label}
              <span class="ml-1 font-mono text-[0.7rem] opacity-70">{counts[f.id] ?? 0}</span>
            </button>
          {/each}
        </div>
        <input
          type="search"
          class="input input-bordered input-sm w-full bg-base-100 sm:w-64"
          placeholder="Search name, email, community, code…"
          bind:value={query}
          autocomplete="off"
        />
      </div>

      <div class="mt-4 overflow-x-auto rounded-box border hair bg-base-100">
        <table class="table table-sm">
          <thead>
            <tr class="text-[0.65rem] uppercase tracking-wide text-base-content/62">
              {@render sortHead('name', 'Account', '')}
              <th class="font-normal">Communities &amp; plan</th>
              <th class="hidden font-normal md:table-cell">Affiliate</th>
              {@render sortHead('seen', 'Last seen', 'hidden whitespace-nowrap sm:table-cell')}
              {@render sortHead('joined', 'Joined', 'hidden whitespace-nowrap lg:table-cell')}
            </tr>
          </thead>
          <tbody>
            {#if visible.length === 0}
              <tr>
                <td colspan="5" class="py-6 text-center text-sm text-base-content/68">
                  No account matches that.
                </td>
              </tr>
            {:else}
              {#each visible as a (a.user_id)}
                {@const dot = presence(a)}
                <!-- The row is the control: focusable, Enter/Space opens the drawer,
                     and the name picks up the accent on hover so it reads as one. -->
                <tr
                  class="group cursor-pointer align-top transition-colors hover:bg-base-200/45
                    focus-visible:bg-base-200/60 focus-visible:outline-none {openId === a.user_id
                    ? 'bg-base-200/60'
                    : ''}"
                  tabindex="0"
                  role="button"
                  aria-label="Open {a.name}"
                  onclick={() => (openId = a.user_id)}
                  onkeydown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openId = a.user_id;
                    }
                  }}
                >
                  <td>
                    <div class="flex items-start gap-2.5">
                      <span
                        class="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full {dot.cls}"
                        title={dot.title}
                      ></span>
                      <div class="min-w-0">
                        <div class="flex items-center gap-1.5">
                          <span class="redact truncate text-sm font-medium transition-colors group-hover:text-primary">
                            {a.name}
                          </span>
                          {#if a.role === 'superadmin'}
                            <span class="eyebrow rounded bg-base-content/8 px-1 py-px text-[0.55rem]">staff</span>
                          {:else if a.plan_status === 'free_for_life'}
                            <span
                              class="eyebrow rounded bg-info/10 px-1 py-px text-[0.55rem] text-info"
                              title="Granted by hand — every community this account holds is free for life, and none of it counts as revenue"
                            >free for life</span>
                          {/if}
                        </div>
                        <div class="redact truncate font-mono text-[0.7rem] text-base-content/62">{a.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {#if a.communities?.length}
                      <div class="space-y-1">
                        {#each a.communities as c (c.id)}
                          <div class="flex flex-wrap items-center gap-1.5">
                            <span class="redact text-sm">{c.name}</span>
                            <PlanBadge status={c.plan_status} trialEndsAt={c.trial_ends_at} compact />
                            <span class="font-mono text-[0.65rem] text-base-content/55">
                              {c.total_members ?? '—'}
                            </span>
                          </div>
                        {/each}
                      </div>
                    {:else if a.pending_skool_url}
                      <span class="text-[0.75rem] text-warning">Awaiting onboarding</span>
                    {:else if a.role === 'superadmin'}
                      <span class="text-[0.75rem] text-base-content/55">Every community, by role</span>
                    {:else}
                      <span class="text-base-content/38">—</span>
                    {/if}
                  </td>
                  <td class="hidden md:table-cell">{@render affiliateCell(a)}</td>
                  <td
                    class="hidden whitespace-nowrap text-xs sm:table-cell {a.last_login_at
                      ? 'text-base-content/72'
                      : 'text-base-content/45'}"
                  >
                    {lastSeen(a)}
                  </td>
                  <td class="hidden whitespace-nowrap text-xs text-base-content/72 lg:table-cell">
                    {timeAgo(a.created_at ?? null, now)}
                  </td>
                </tr>
              {/each}
            {/if}
          </tbody>
        </table>
      </div>

      <p class="mt-3 text-xs text-base-content/55">
        Showing {visible.length} of {accounts.length}. Invite links live on
        <button class="link link-hover text-primary" onclick={() => navigate('/admin/invites')}>Invites</button>;
        commissions and payouts on
        <button class="link link-hover text-primary" onclick={() => navigate('/admin/affiliates')}>Affiliates</button>.
      </p>
    </PageContainer>
  {/if}

  <!-- Detail + the two levers. They sit here rather than in the row so the table
       stays a table: nothing in it is clickable except the row itself. -->
  <Drawer open={!!selected} onclose={() => (openId = null)} label="Account detail">
    {#if selected}
      {@const a = selected}
      <div class="flex items-start justify-between gap-3 border-b hair px-5 py-4">
        <div class="min-w-0">
          <div class="eyebrow">{a.role === 'superadmin' ? 'Staff' : 'Creator'}</div>
          <h2 class="redact mt-0.5 truncate font-display text-xl font-semibold">{a.name}</h2>
          <div class="redact truncate font-mono text-xs text-base-content/68">{a.email}</div>
        </div>
        <button class="btn btn-ghost btn-sm btn-square" onclick={() => (openId = null)} aria-label="Close">✕</button>
      </div>

      <div class="space-y-6 px-5 py-5">
        <!-- Activity -->
        <section>
          <div class="eyebrow mb-2">Activity</div>
          <dl class="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt class="text-[0.7rem] text-base-content/62">Last signed in</dt>
              <dd class="mt-0.5 {a.last_login_at ? '' : 'text-base-content/55'}">
                {lastSeen(a)}
                {#if a.last_login_at}
                  <span class="block text-[0.7rem] text-base-content/55">{fmtDate(a.last_login_at)}</span>
                {/if}
              </dd>
            </div>
            <div>
              <dt class="text-[0.7rem] text-base-content/62">Joined</dt>
              <dd class="mt-0.5">
                {timeAgo(a.created_at ?? null, now)}
                <span class="block text-[0.7rem] text-base-content/55">{fmtDate(a.created_at)}</span>
              </dd>
            </div>
          </dl>
        </section>

        <!-- Billing -->
        <section>
          <div class="eyebrow mb-2">Billing</div>
          {#if a.role === 'superadmin'}
            <p class="text-sm text-base-content/68">Staff accounts are never billed or gated.</p>
          {:else}
            <p class="text-sm text-base-content/72">
              Billing is per community. Making the account free for life overrides all of
              them at once — use it for a giveaway, or for a manual arrangement that isn't
              running through Stripe. It does not count as revenue anywhere.
            </p>
            <div class="mt-3 flex items-center gap-3">
              <button
                class="btn btn-sm {a.plan_status === 'free_for_life' ? 'btn-ghost' : 'btn-primary'}"
                onclick={() => toggleComp(a)}
                disabled={busy}
              >
                {#if busy}<span class="loading loading-spinner loading-xs"></span>{/if}
                {a.plan_status === 'free_for_life' ? 'Remove' : 'Make free for life'}
              </button>
              {#if a.paid_at}
                <span class="text-xs text-base-content/62">Free for life since {fmtDate(a.paid_at)}</span>
              {/if}
            </div>
          {/if}
        </section>

        <!-- Communities -->
        <section>
          <div class="eyebrow mb-2">Communities</div>
          {#if a.communities?.length}
            <ul class="space-y-2">
              {#each a.communities as c (c.id)}
                <li class="flex items-center justify-between gap-3 rounded-field border hair px-3 py-2">
                  <div class="min-w-0">
                    <div class="redact truncate text-sm font-medium">{c.name}</div>
                    <div class="font-mono text-[0.65rem] text-base-content/55">
                      <span class="redact">{c.slug}</span> · {c.total_members ?? '—'} members
                    </div>
                  </div>
                  <PlanBadge status={c.plan_status} trialEndsAt={c.trial_ends_at} compact />
                </li>
              {/each}
            </ul>
          {:else if a.pending_skool_url}
            <p class="text-sm text-base-content/72">
              Onboarding hasn't finished. Claimed
              <span class="font-mono text-xs">{a.pending_skool_url}</span>.
            </p>
          {:else}
            <p class="text-sm text-base-content/68">
              {a.role === 'superadmin'
                ? 'Staff see every community by role, so none are granted here.'
                : 'None yet.'}
            </p>
          {/if}
          {#if a.role !== 'superadmin'}
            <button class="btn btn-sm mt-3" onclick={() => openCommunities(a)}>Manage communities</button>
          {/if}
        </section>

        <!-- Sign-in link -->
        <section>
          <div class="eyebrow mb-2">Sign-in link</div>
          <p class="text-sm text-base-content/72">
            {#if a.last_login_at}
              A one-time link that signs <span class="redact">{a.name}</span> in, optionally granting more communities
              at the same time.
            {:else}
              <span class="redact">{a.name}</span> has never signed in. A link gets them in without a password — which
              matters for an account created by an invite, since it has no password to
              reset yet.
            {/if}
          </p>
          <button class="btn btn-sm mt-3" onclick={() => openInvite(a)}>Create invite link</button>
        </section>

        <!-- Affiliate -->
        <section>
          <div class="eyebrow mb-2">Affiliate</div>
          {#if a.affiliate}
            <div class="flex flex-wrap items-center gap-2">
              <span class="redact rounded bg-base-200/70 px-1.5 py-0.5 font-mono text-xs">{a.affiliate.code}</span>
              <span class="text-sm text-base-content/72">
                {fmtRate(a.affiliate.rate_bps)}{a.affiliate.has_custom_rate ? ' · special deal' : ' · default rate'}
              </span>
              {#if a.affiliate.disabled}
                <span class="rounded bg-base-content/10 px-1.5 py-0.5 text-[0.7rem] text-base-content/72">
                  Earning off
                </span>
              {/if}
            </div>
            <dl class="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
              {#each [['Referred', `${a.affiliate.referred_count}`], ['Payable', fmtUsd(a.affiliate.totals.payable_cents)], ['Pending', fmtUsd(a.affiliate.totals.pending_cents)], ['Paid out', fmtUsd(a.affiliate.totals.paid_cents)]] as [label, value] (label)}
                <div>
                  <dt class="text-[0.7rem] text-base-content/62">{label}</dt>
                  <dd class="mt-0.5 font-mono">{value}</dd>
                </div>
              {/each}
            </dl>
            <button class="btn btn-ghost btn-sm mt-2 px-1" onclick={() => navigate('/admin/affiliates')}>
              Rates and payouts →
            </button>
          {:else}
            <p class="text-sm text-base-content/68">Not enrolled.</p>
            <button class="btn btn-ghost btn-sm mt-1 px-1" onclick={() => navigate('/admin/affiliates')}>
              Enroll on Affiliates →
            </button>
          {/if}
          {#if a.referred_by}
            <p class="mt-3 text-sm text-base-content/72">
              Referred by <span class="redact font-medium">{a.referred_by.name}</span>
              {#if a.referred_by.code}
                <span class="redact font-mono text-xs text-base-content/62">({a.referred_by.code})</span>
              {/if}
            </p>
          {/if}
        </section>
      </div>
    {/if}
  </Drawer>

  <!-- Invite someone -->
  {#if inviting}
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-base-300/60 p-4">
      <div class="w-full max-w-md rounded-box border hair bg-base-100 p-5 shadow-lg">
        {#if lastInvite}
          <!-- The link IS the deliverable, so it gets the whole dialog once it exists.
               Nothing is emailed yet — sending it is the sender's job for now. -->
          <div class="eyebrow">Invite ready</div>
          <p class="mt-1 text-sm text-base-content/72">
            Send this to <span class="redact font-mono text-xs">{lastInvite.email}</span>. It works once,
            signs them in without a password, and {expiresIn(lastInvite.expires_at)}.
          </p>
          <div class="mt-3 flex items-center gap-2">
            <input
              type="text"
              readonly
              class="input input-bordered input-sm flex-1 bg-base-100 font-mono text-xs"
              value={linkFor(lastInvite.token)}
              onfocus={(e) => (e.target as HTMLInputElement).select()}
            />
            <button class="btn btn-sm" onclick={() => copyLink(lastInvite!.token)}>
              {copied === lastInvite.token ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <div class="mt-4 flex justify-end gap-2">
            <button class="btn btn-ghost btn-sm" onclick={() => openInvite(null)}>Invite another</button>
            <button class="btn btn-primary btn-sm" onclick={() => (inviting = false)}>Done</button>
          </div>
        {:else}
          <div class="eyebrow">{inviteFor ? 'Invite link' : 'Invite someone'}</div>
          <p class="mt-1 text-sm text-base-content/72">
            {#if inviteFor}
              A one-time link that signs <span class="redact font-medium">{inviteFor.name}</span> in.
              Tick any communities to add at the same time — or send it as-is if they just
              need a way back in.
            {:else}
              Creates a one-time link that signs them in and gives them these communities. The
              email doesn't need an account yet — the link makes one.
            {/if}
          </p>

          <label class="mt-3 block">
            <span class="eyebrow mb-1.5 block text-[0.6rem]">Their email</span>
            <input
              type="email"
              class="input input-bordered input-sm w-full bg-base-100 font-mono text-xs
                {inviteFor ? 'cursor-not-allowed opacity-70' : ''}"
              bind:value={inviteEmail}
              readonly={!!inviteFor}
              placeholder="them@example.com"
              autocomplete="off"
            />
          </label>

          <div class="mt-3">
            <span class="eyebrow mb-1.5 block text-[0.6rem]">Communities</span>
            <input
              class="input input-sm input-bordered w-full"
              placeholder="Search every community…"
              value={commSearch}
              oninput={onCommSearch}
              autocomplete="off"
            />
            <div class="mt-2 max-h-48 overflow-y-auto rounded-box border hair">
              {#if commLoading && commResults.length === 0}
                <div class="px-3 py-4 text-sm text-base-content/68">Searching…</div>
              {:else}
                {#each commResults as c (c.id)}
                  {@const already = inviteHolds.has(c.id)}
                  <!-- A community they already administer is shown, disabled: hiding
                       it would read as "we don't have that one", and re-granting it
                       is a no-op the sender shouldn't have to think about. -->
                  <label
                    class="flex items-center gap-2 border-b hair px-3 py-2 last:border-b-0
                      {already ? 'opacity-55' : 'cursor-pointer hover:bg-base-200/40'}"
                  >
                    <input
                      type="checkbox"
                      class="checkbox checkbox-xs"
                      checked={already || invitePicked.has(c.id)}
                      disabled={already}
                      onchange={() => toggleInvitePick(c.id)}
                    />
                    <span class="redact text-sm">{c.name}</span>
                    {#if already}
                      <span class="eyebrow text-[0.55rem] text-base-content/55">has</span>
                    {/if}
                    <span class="ml-auto font-mono text-[0.65rem] text-base-content/55">
                      {c.total_members ?? '—'}
                    </span>
                  </label>
                {:else}
                  <div class="px-3 py-4 text-sm text-base-content/68">No communities match.</div>
                {/each}
              {/if}
            </div>
            <p class="mt-1.5 text-[0.7rem] text-base-content/55">
              Over 100 members starts a 30-day trial on that community; under stays free.
            </p>
          </div>

          {#if inviteError}
            <p class="mt-2 text-sm text-error">{inviteError}</p>
          {/if}

          <div class="mt-4 flex items-center justify-between">
            <span class="text-xs text-base-content/62">
              {#if invitePicked.size === 0 && inviteFor}
                Sign-in link only
              {:else}
                {invitePicked.size} to add
              {/if}
            </span>
            <div class="flex gap-2">
              <button class="btn btn-ghost btn-sm" onclick={() => (inviting = false)} disabled={inviteBusy}>
                Cancel
              </button>
              <button
                class="btn btn-primary btn-sm"
                onclick={submitInvite}
                disabled={inviteBusy || !canSubmitInvite}
              >
                {#if inviteBusy}<span class="loading loading-spinner loading-xs"></span>{/if}
                Create link
              </button>
            </div>
          </div>
        {/if}
      </div>
    </div>
  {/if}

  <!-- Manage communities -->
  {#if editing}
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-base-300/60 p-4">
      <div class="w-full max-w-md rounded-box border hair bg-base-100 p-5 shadow-lg">
        <div class="eyebrow">Communities</div>
        <p class="mt-1 text-sm text-base-content/72">
          Which communities <span class="redact font-mono text-xs">{editing.email}</span> can administer.
        </p>

        <input
          class="input input-sm input-bordered mt-3 w-full"
          placeholder="Search every community…"
          value={commSearch}
          oninput={onCommSearch}
          autocomplete="off"
        />

        <div class="mt-2 max-h-64 overflow-y-auto rounded-box border hair">
          {#if commLoading && commMatches.length === 0}
            <div class="px-3 py-4 text-sm text-base-content/68">Searching…</div>
          {:else}
            {#each commMatches as c (c.id)}
              <label
                class="flex cursor-pointer items-center gap-2 border-b hair px-3 py-2 last:border-b-0 hover:bg-base-200/40"
              >
                <input
                  type="checkbox"
                  class="checkbox checkbox-xs"
                  checked={picked.has(c.id)}
                  onchange={() => togglePick(c.id)}
                />
                <span class="redact text-sm">{c.name}</span>
                <span class="ml-auto font-mono text-[0.65rem] text-base-content/55">{c.slug}</span>
              </label>
            {:else}
              <div class="px-3 py-4 text-sm text-base-content/68">No communities match.</div>
            {/each}
          {/if}
        </div>

        {#if saveError}
          <p class="mt-2 text-sm text-error">{saveError}</p>
        {/if}

        <div class="mt-4 flex items-center justify-between">
          <span class="text-xs text-base-content/62">{picked.size} selected</span>
          <div class="flex gap-2">
            <button class="btn btn-ghost btn-sm" onclick={() => (editing = null)} disabled={saving}>Cancel</button>
            <button class="btn btn-primary btn-sm" onclick={saveCommunities} disabled={saving}>
              {#if saving}<span class="loading loading-spinner loading-xs"></span>{/if}
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  {/if}
</AdminShell>
